import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { Location } from "@angular/common";
import { fadeInOut } from "../../../rubbl-shared/animations";
import { EnvironmentConfig, MarketTrackingEvents, ShareService } from "../../../core/core";
import { CREDIT_APPLICATION_FILE_NAME, CREDIT_APPLICATION_PATH } from "../../../core/models/credit-application.constants";
import { InventoryItem } from "../../../inventory/models/inventory-item.model";
import { Attachment } from "../../../inventory/models/attachment.model";
import { SwiperConfigInterface } from "ngx-swiper-wrapper";
import { PaginationOptions } from "swiper/types/components/pagination";
import { InventoryService, PhotoFilterService } from "../../../inventory/inventory";

declare const environment: EnvironmentConfig;

@Component({
  selector: "common-buy-only-machine-card-advanced",
  templateUrl: "./buy-only-machine-card-advanced.component.html",
  styleUrls: ["./buy-only-machine-card-advanced.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInOut("250ms", "250ms")],
})
export class BuyOnlyMachineCardAdvancedComponent implements OnInit {
  @Input() machine: InventoryItem;
  @Input() filteredAttachments: string[] = [];

  readonly creditApplicationPath = CREDIT_APPLICATION_PATH;
  readonly creditApplicationFileName = CREDIT_APPLICATION_FILE_NAME;
  MarketTrackingEvents = MarketTrackingEvents; // I may still need this? Tracks other things besides add to cart?
  fixedAttachments: Attachment[] = [];
  nonFixedAttachments: Attachment[] = [];
  availableAndSelected: Attachment[] = [];

  showExtraInfo: boolean = false;
  extraInfoHeight: string;
  baseImagePath = environment.baseImagePath;
  hasStockPhotos: boolean = false;
  imageLoadError: boolean = false;
  failedImageIndices: Set<number> = new Set();

  @ViewChild("card") card;

  extraInfoIndex = 0;
  paginationConfig: PaginationOptions = {
    type: "bullets",
    el: ".swiper-pagination",
    bulletActiveClass: "swiper-pagination-bullet-active active-bullet",
    bulletClass: "swiper-pagination-bullet inactive-bullet",
    clickable: true,
    modifierClass: "swiper-pagination-bullets bottom-0",
  };

  extraInfoConfig: SwiperConfigInterface = {
    pagination: this.paginationConfig,
  };

  imageSwipeConfig: SwiperConfigInterface = {
    loop: true,
    pagination: this.paginationConfig,
    scrollbar: false,
    navigation: true,
    lazy: {
      preloaderClass: "preloader",
    },
  };
  machineDetailsUrl: string;

  /** Thumbnails rendered in the card carousel (up to three, lazy-loaded when there is more than one). */
  cardThumbnails: string[] = [];

  constructor(
    private cd: ChangeDetectorRef,
    private inventoryService: InventoryService,
    private shareService: ShareService,
    private router: Router,
    private location: Location,
    private photoFilterService: PhotoFilterService,
  ) {}

  ngOnInit() {
    // Filter thumbnails based on the new photo display logic
    this.machine.thumbnails = this.photoFilterService.filterPhotos(this.machine.thumbnails, this.machine.primaryType);

    if (this.machine.thumbnails?.length === 1) {
      this.imageSwipeConfig.pagination = false;
      this.imageSwipeConfig.navigation = false;
    }

    this.cardThumbnails = (this.machine.thumbnails ?? []).slice(0, 3);

    const firstThumbNail = this.machine?.thumbnails[0];
    if (firstThumbNail && firstThumbNail.includes("pubweb/catalog")) {
      this.hasStockPhotos = true;
    }

    const currentPath = this.location.path();
    this.machineDetailsUrl = this.inventoryService.machineDetailsUrl(this.machine, false, currentPath);

    // Filter only available attachments and distinct by display name
    this.machine.relatedAttachments = this.machine.relatedAttachments.filter(
      (value, index, self) => index === self.findIndex(t => t.displayName === value.displayName),
    );

    // Figure out what is fixed on the attachments
    this.fixedAttachments = this.machine.relatedAttachments.filter(d => d.isFixed);
    this.nonFixedAttachments = this.machine.relatedAttachments.filter(d => !d.isFixed);

    let installFirst = true;
    for (const filteredAttachment of this.filteredAttachments) {
      const attachment = this.machine.relatedAttachments.find(d => d.size === filteredAttachment);
      if (!attachment) {
        continue;
      }

      // If we have a filtered attachment, select it
      attachment.selected = true;

      // Install the first one
      if (installFirst) {
        attachment.install = true;
        installFirst = false;

        // If the attachment is standard, set the cost to 0
        if (attachment.standard) {
          attachment.price = attachment.cost = 0;
        }
      }

      this.availableAndSelected.push(attachment);
    }
  }

  showExtraInfoclick(index: number = 0) {
    this.imageSwipeConfig.pagination = false;
    this.extraInfoHeight = this.card.nativeElement["offsetHeight"] + 10 + "px";
    this.showExtraInfo = true;

    this.extraInfoIndex = index;

    const modalBody = document.querySelector(".modal-body");
    if (modalBody) {
      modalBody.addEventListener(
        "scroll",
        e => {
          this.hideExtraInfo();
        },
        { once: true },
      );
    } else {
      // Add event listener to scroll
      document.addEventListener(
        "scroll",
        e => {
          this.hideExtraInfo();
        },
        { once: true },
      );
    }
  }

  hideExtraInfo() {
    this.imageSwipeConfig.pagination = this.paginationConfig;
    this.showExtraInfo = false;
    this.cd.detectChanges();
  }

  showStockPhotoInfo() {
    const hasAttachments = this.fixedAttachments.length || this.nonFixedAttachments.length;
    if (hasAttachments) {
      this.showExtraInfoclick(2);
    } else {
      this.showExtraInfoclick(1);
    }
  }

  share() {
    const currentPath = this.location.path();
    this.shareService.share(
      `Check out this ${this.machine.make} on Rubbl!`,
      `A ${this.machine.year} ${this.machine.make} ${this.machine.model} near ${this.machine.location.name}.`,
      this.inventoryService.machineDetailsUrl(this.machine, true, currentPath),
      { target: "searchResults-machineShare" },
    );
  }

  navigateToFinancing(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate([this.machineDetailsUrl], {
      queryParams: { id: this.machine.id },
      fragment: "financing-calculator",
    });
  }

  onImageError(index: number) {
    this.failedImageIndices.add(index);
    // Only show error message if all rendered images have failed
    if (this.failedImageIndices.size >= (this.cardThumbnails?.length || 0)) {
      this.imageLoadError = true;
    }
    this.cd.detectChanges();
  }

  shouldShowImage(index: number): boolean {
    return !this.failedImageIndices.has(index);
  }
}
